import React from 'react';
import { motion } from 'framer-motion';

interface LoadingScreenProps {
  message?: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message = 'Loading...' }) => {
  // Animation variants for the container
  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        when: 'afterChildren',
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  // Animation variants for the logo
  const logoVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
    },
  };

  // Animation variants for the dots
  const dotVariants = {
    initial: { y: 0, opacity: 0 },
    animate: {
      y: [0, -10, 0],
      opacity: 1,
      transition: {
        y: {
          repeat: Infinity,
          duration: 1,
          ease: 'easeInOut',
          repeatType: 'reverse',
        },
      },
    },
  };

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center bg-dark z-50"
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Logo animation */}
      <motion.div className="mb-8" variants={logoVariants}>
        <svg
          className="w-20 h-20 text-primary"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="50" cy="50" r="48" fill="currentColor" fillOpacity="0.1" />
          <motion.path
            d="M35,25 L20,50 L35,75"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 1.5,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'loop',
              repeatDelay: 0.5,
            }}
          />
          <motion.path
            d="M65,25 L80,50 L65,75"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 1.5,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'loop',
              repeatDelay: 0.5,
            }}
          />
          <motion.path
            d="M55,25 L45,75"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 1.5,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'loop',
              repeatDelay: 0.5,
            }}
          />
        </svg>
      </motion.div>

      {/* Loading text with animated dots */}
      <div className="flex items-center">
        <motion.h2
          className="text-xl font-medium text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {message}
        </motion.h2>
        <div className="flex ml-2">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              className="w-2 h-2 mx-0.5 rounded-full bg-primary"
              variants={dotVariants}
              initial="initial"
              animate="animate"
              transition={{
                delay: i * 0.2,
              }}
            />
          ))}
        </div>
      </div>

      {/* Subtle loading text */}
      <motion.p
        className="mt-4 text-sm text-gray-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        Preparing your code snippets
      </motion.p>

      {/* Animated glow effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.05, 0.1, 0.05],
          transition: {
            duration: 3,
            repeat: Infinity,
            repeatType: 'reverse',
          },
        }}
      >
        <div className="absolute inset-0 bg-gradient-radial from-primary-500/10 to-transparent" />
      </motion.div>
    </motion.div>
  );
};

export default LoadingScreen;
